import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getDealers } from "@insite/client-framework/Services/DealerService";
import siteMessage from "@insite/client-framework/SiteMessage";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import translate from "@insite/client-framework/Translate";
import { CountryModel, DealerModel } from "@insite/client-framework/Types/ApiModels";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Button from "@insite/mobius/Button";
import { OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal from "@insite/mobius/Modal";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import UcsReCaptcha from "@ucs/Components/UcsReCaptcha";
import ucsAccountRequest, { AccountRequestModel } from "@ucs/Handlers/UcsAccountRequest";
import { ucsGetCountriesAndStates } from "@ucs/Handlers/UcsCustomApis";
import { SampleAddress } from "@ucs/Handlers/UcsGuestSampleRequest";
import { formatPhoneNumber } from "@ucs/Helpers/Formatting";
import {
    validateAccountNumberField,
    validateAddress1,
    validateAddress2,
    validateCity,
    validateCompanyNameField,
    validateCountry,
    validateEmailField,
    validateExistingCustomerValue,
    validateFaxField,
    validateFirstNameField,
    validateLastNameField,
    validatePhoneField,
    validatePostalCode,
    validateState,
    validateStateName,
    validateTradeOrDbaNameField,
} from "@ucs/Helpers/FormFieldsValidation";
import { TextFieldPresentationProps } from "@ucs/MobiusOverrides/TextField/TextField";
import customLoadDealers from "@ucs/Store/Custom/CustomLoadDealers";
import { getDealersDropdown, getGeoPermission } from "@ucs/Store/CustomSelectors";
import { CustomState } from "@ucs/Store/Reducers";
import React, { ChangeEvent, RefObject, useContext } from "react";
import { FC, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    successTitle: string;
    successMessage: string;
    submitButtonText: string;
    extendedStyles?: AccountRequestFormStyles;
    redirectOnSubmit:  LinkFieldValue;
}
const mapDispatchToProps = {
    ucsAccountRequest,
    customLoadDealers,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
};
const mapStateToProps = (state: CustomState) => {
    return {
        isGeoPermissionDenied: getGeoPermission(state),
        dealersDropdown: getDealersDropdown(state),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface AccountRequestFormStyles {
    formWrapper?: InjectableCss;
    formGroup?: InjectableCss;
    formGroupFullWidth?: InjectableCss;
    textFieldStyles?: TextFieldPresentationProps;
    selectStyles?: SelectPresentationProps;
    buttonGridItem?: GridItemProps;
    buttonSection?: InjectableCss;
    locationSelect?: SelectPresentationProps;
}

export const accountRequestFormStyles: AccountRequestFormStyles = {
    formWrapper: {
        css: css`
            width: 100%;
            & > * {
                margin-bottom: 15px;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding-left: 36px;
                        padding-right: 36px;
                    `,
                ]);
            }};
        `,
    },
    formGroup: {
        css: css`
            width: 100%;
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 100%;
                        gap: 15px;
                    `,
                    css`
                        grid-template-columns: 100%;
                        gap: 15px;
                    `,
                ]);
            }}
        `,
    },
    formGroupFullWidth: {
        css: css`
            width: 100%;
            display: grid;
            gap: 20px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 100%;
                        gap: 15px;
                    `,
                    css`
                        grid-template-columns: 100%;
                        gap: 15px;
                    `,
                ]);
            }}
        `,
    },
    textFieldStyles: {
        labelProps: {
            size: 13,
            weight: 700,
            css: css`
                line-height: 16px;
                letter-spacing: 0.1em;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 6px !important;
            `,
        },
    },
    selectStyles: {
        labelProps: {
            size: 13,
            weight: 700,
            css: css`
                line-height: 16px;
                letter-spacing: 0.1em;
            `,
        },
        cssOverrides: {

            formInputWrapper: css`
                margin-top: 6px !important;
             `
        },
    },
    buttonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    buttonSection: {
        css: css`
            display: flex;
            gap: 10px;
            margin-top: 32px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        & > *:last-child {
                            flex-grow: 1;
                        }
                        padding-bottom: 30px;
                    `,
                ]);
            }}
        `,
    },
};

const AccountRequestRequestForm: FC<Props> = ({
    extendedStyles,
    successMessage,
    successTitle,
    submitButtonText,
    ucsAccountRequest,
    customLoadDealers,
    isGeoPermissionDenied,
    dealersDropdown,
    redirectOnSubmit,
    history,
    validateReCaptcha,
}) => {
    const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef();
    const [styles] = useState(() => mergeToNew(accountRequestFormStyles, extendedStyles));
    const [formLoaded, setFormLoaded] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Selected countries
    const [countries, setCountries] = useState<CountryModel[]>();
    const [companySelectedCountry, setCompanySelectedCountry] = useState<CountryModel>();

    // personal info
    const [emailAddress, setEmailAddress] = useState("");
    const [emailError, setEmailError] = useState("");
    const [firstName, setFirstName] = useState("");
    const [firstNameError, setFirstNameError] = useState("");
    const [lastName, setLastName] = useState("");
    const [lastNameError, setLastNameError] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [faxNumber, setFaxNumber] = useState("");
    const [faxError, setFaxError] = useState("");

    // nearby location
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [selectedLocation, setSelectedLocation] = useState("");
    const [locationError, setLocationError] = useState<React.ReactNode>("");
    
    // company info
    const [companyName, setCompanyName] = useState("");
    const [companyNameError, setCompanyNameError] = useState("");
    const [tradeOrDbaName, setTradeOrDbaName] = useState("");
    const [tradeOrDbaNameError, setTradeOrDbaNameError] = useState("");
    const [existingCustomer, setExistingCustomer] = useState("");
    const [existingCustomerError, setExistingCustomerError] = useState("");
    const [accountNumber, setAccountNumber] = useState("");
    const [accountNumberError, setAccountNumberError] = useState("");

    // Address - only of not existing customer
    const [companyAddress, setCompanyAddress] = useState<SampleAddress>({});
    const [companyAddress1Error, setCompanyAddress1Error] = useState("");
    const [companyAddress2Error, setCompanyAddress2Error] = useState("");
    const [companyCountryError, setCompanyCountryError] = useState("");
    const [companyStateError, setCompanyStateError] = useState("");
    const [companyCityError, setCompanyCityError] = useState("");
    const [companyPostalCodeError, setCompanyPostalCodeError] = useState("");

    const toasterContext = useContext(ToasterContext);

    const isExistingCustomerOptions = ["Yes", "No"];

    const getSelectedLocation=()=>{      
        if (isGeoPermissionDenied === "No") {
            setSelectedLocation(getCookie("SelectedLocation") || "");
        } else {
            setSelectedLocation("");
        }        
    }

    React.useEffect(() => {
        const updateSelectedLocationFromCookie = () => {
            if (isGeoPermissionDenied === "No") {
                setSelectedLocation(getCookie("SelectedLocation") || "");
            } else {
                setSelectedLocation("");
            }
        };

        updateSelectedLocationFromCookie();

        // Listen for storage events (cross-tab)
        const onStorage = (event: StorageEvent) => {
            if (event.key === "SelectedLocation") {
                updateSelectedLocationFromCookie();
            }
        };
        window.addEventListener("storage", onStorage);

        // Poll for same-tab cookie changes
        let lastCookie = getCookie("SelectedLocation");
        const interval = setInterval(() => {
            const currentCookie = getCookie("SelectedLocation");
            if (currentCookie !== lastCookie) {
                lastCookie = currentCookie;
                updateSelectedLocationFromCookie();
            }
        }, 1000);

        return () => {
            window.removeEventListener("storage", onStorage);
            clearInterval(interval);
        };
    }, [isGeoPermissionDenied]);

    // Effects
    React.useEffect(() => {
        customLoadDealers({} as any);
        setFormLoaded(true);
        if (countries === undefined) {
            ucsGetCountriesAndStates({}).then((value: any) => {
                if (value && value.countries) {
                    setCountries(value.countries);
                }
            });
        }  
    }, []);

    React.useEffect(() => {        
        
        const locationDropdownData = dealersDropdown.dealers || [];
        const locationOptions = locationDropdownData.map((item: DealerModel) => ({
            optionValue: `${item.name}, ${item.state}`,
            optionText: `${item.name}, ${item.state}`,
        }));
        setOptions(locationOptions);
    }, [isGeoPermissionDenied, dealersDropdown]);

    // personal info
    React.useEffect(() => {
        if (formLoaded && typeof emailAddress !== "undefined") {
            if (emailAddress!.length === 0 || emailAddress!.length > 4) {
                validateEmail();
            }
        }
    }, [emailAddress]);

    React.useEffect(() => {
        if (formLoaded && typeof firstName !== "undefined") {
            if (firstName!.length === 0 || firstName!.length >= 2) {
                validateFirstName();
            }
        }
    }, [firstName]);

    React.useEffect(() => {
        if (formLoaded && typeof lastName !== "undefined") {
            if (lastName!.length === 0 || lastName!.length >= 2) {
                validateLastName();
            }
        }
    }, [lastName]);

    // company info
    React.useEffect(() => {
        if (formLoaded && typeof companyName !== "undefined") {
            if (companyName!.length === 0 || companyName!.length >= 2) {
                validateCompanyName();
            }
        }
    }, [companyName]);

    React.useEffect(() => {
        if (formLoaded && typeof existingCustomer !== "undefined") {
            validateExistingCustomer();
        }
    }, [existingCustomer]);

    React.useEffect(() => {
        if (formLoaded && existingCustomer === "Yes" && typeof accountNumber !== "undefined") {
            validateAccountNumber();
        }
    }, [accountNumber]);

    // trade or dba account
    React.useEffect(() => {
        if (formLoaded && existingCustomer === "No" && typeof tradeOrDbaName !== "undefined") {
            if (tradeOrDbaName!.length === 0 || tradeOrDbaName!.length >= 2) {
                validateTradeOrDbaName();
            }
        }
    }, [tradeOrDbaName]);

    // company address
    React.useEffect(() => {
        if (formLoaded && existingCustomer === "No" && typeof companyAddress.addressLine1 !== "undefined") {
            validateCompanyAddress1();
        }
    }, [companyAddress.addressLine1]);

    React.useEffect(() => {
        if (formLoaded && existingCustomer === "No" && typeof companyAddress.addressLine2 !== "undefined") {
            validateCompanyAddress2();
        }
    }, [companyAddress.addressLine2]);

    React.useEffect(() => {
        if (formLoaded && existingCustomer === "No" && typeof companyAddress.countryId !== "undefined") {
            validateCompanyCountry();
            companyAddress.postalCode && validateCompanyPostalCode()
        }
    }, [companySelectedCountry, companyAddress.countryId]);

    React.useEffect(() => {
        if (formLoaded && existingCustomer === "No" && typeof companyAddress.stateName !== "undefined") {
            validateCompanyStateName();
        }
    }, [companyAddress.stateName]);

    React.useEffect(() => {
        if (formLoaded && existingCustomer === "No" && typeof companyAddress.stateId !== "undefined") {
            validateCompanyState();
        }
    }, [companyAddress.stateId]);

    React.useEffect(() => {
        if (formLoaded && existingCustomer === "No" && typeof companyAddress.city !== "undefined") {
            validateCompanyCity();
        }
    }, [companyAddress.city]);

    React.useEffect(() => {
        if (formLoaded && existingCustomer === "No" && typeof companyAddress.postalCode !== "undefined") {
            validateCompanyPostalCode();
        }
    }, [companyAddress.postalCode]);

    // validation personal
    const validateEmail = () => {
        const errorMessage = validateEmailField(emailAddress);
        setEmailError(errorMessage);
        return !errorMessage;
    };

    const validateFirstName = () => {
        const errorMessage = validateFirstNameField(firstName, true);
        setFirstNameError(errorMessage);
        return !errorMessage;
    };

    const validateLastName = () => {
        const errorMessage = validateLastNameField(lastName, true);
        setLastNameError(errorMessage);
        return !errorMessage;
    };

    const validatePhone = () => {
        const phoneFormatted: string = formatPhoneNumber(phoneNumber);
        setPhoneNumber(phoneFormatted);
        const errorMessage = validatePhoneField(phoneFormatted);
        setPhoneError(errorMessage);
        return !errorMessage;
    };
    const validateFax = () => {
        const faxFormatted: string = formatPhoneNumber(faxNumber);
        setFaxNumber(faxFormatted);
        const errorMessage = validateFaxField(faxFormatted);
        setFaxError(errorMessage);
        return !errorMessage;
    };
    const validateLocation = () => {
        const errorMessage = !selectedLocation ? translate("Please select a nearest location.") : "";
        setLocationError(errorMessage);
        return !errorMessage;
    };
    // validation - company
    const validateCompanyName = () => {
        const errorMessage = validateCompanyNameField(companyName, true);
        setCompanyNameError(errorMessage);
        return !errorMessage;
    };

    const validateTradeOrDbaName = () => {
        const errorMessage = validateTradeOrDbaNameField(tradeOrDbaName);
        setTradeOrDbaNameError(errorMessage);
        return !errorMessage;
    };

    const validateExistingCustomer = () => {
        const errorMessage = validateExistingCustomerValue(existingCustomer);
        setExistingCustomerError(errorMessage);
        return !errorMessage;
    };

    const validateAccountNumber = () => {
        const errorMessage = validateAccountNumberField(accountNumber);
        setAccountNumberError(errorMessage);
        return !errorMessage;
    };

    // validate company address
    const validateCompanyAddress1 = () => {
        const errorMessage = validateAddress1(companyAddress.addressLine1, true);
        setCompanyAddress1Error(errorMessage);
        return !errorMessage;
    };

    const validateCompanyAddress2 = () => {
        const errorMessage = validateAddress2(companyAddress.addressLine2, true);
        setCompanyAddress2Error(errorMessage);
        return !errorMessage;
    };

    const validateCompanyCountry = () => {
        const errorMessage = validateCountry(companyAddress.countryId);
        setCompanyCountryError(errorMessage);
        return !errorMessage;
    };

    const validateCompanyState = () => {
        const errorMessage = validateState(companyAddress.stateId, true);
        setCompanyStateError(errorMessage);
        return !errorMessage;
    };

    const validateCompanyStateName = () => {
        const errorMessage = validateStateName(companyAddress.stateName, true);
        setCompanyStateError(errorMessage);
        return !errorMessage;
    };

    const validateCompanyCity = () => {
        const errorMessage = validateCity(companyAddress.city);
        setCompanyCityError(errorMessage);
        return !errorMessage;
    };

    const validateCompanyPostalCode = () => {
        const errorMessage = companySelectedCountry
            ? validatePostalCode(companyAddress.postalCode, companySelectedCountry.abbreviation, true)
            : validatePostalCode(companyAddress.postalCode, "", true);
        setCompanyPostalCodeError(errorMessage);
        return !errorMessage;
    };

    // events - personal
    const emailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailAddress(event.target.value);
    };

    const emailBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        validateEmail();
    };

    const firstNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFirstName(event.target.value);
    };

    const lastNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLastName(event.target.value);
    };

    const phoneChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPhoneNumber(event.target.value);
    };
    const faxChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFaxNumber(event.target.value);
    };

    const phoneBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        validatePhone();
    };
    const faxBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        validateFax();
    };

    const locationChangeHandler = (value?: string) => {
        setSelectedLocation(value || "");
        const errorMessage = !value ? translate("Please select a nearest location.") : "";
        setLocationError(errorMessage);
    };
    const existingCustomerChangeHandler: SelectProps["onChange"] = event => {
        setExistingCustomer(event.target.value);
    };

    const accountNumberChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAccountNumber(event.target.value);
    }

    const tradeOrDbaNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTradeOrDbaName(event.target.value);
    };

    // events - company
    const companyNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCompanyName(event.target.value);
    };

    // events - company address
    const companyAddress1ChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setCompanyAddress({ ...companyAddress, addressLine1: event.target.value });
    };

    const companyAddress2ChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setCompanyAddress({ ...companyAddress, addressLine2: event.target.value });
    };

    const companyCountyChangeHandler: SelectProps["onChange"] = event => {
        const country = countries!.find(c => c.id === event.target.value);

        setCompanySelectedCountry(country);
        setCompanyAddress({ ...companyAddress, countryId: country?.id, stateId: "" });
    };

    const companyStateNameChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setCompanyAddress({ ...companyAddress, stateName: event.target.value });
    };

    const companyStateChangeHandler: SelectProps["onChange"] = event => {
        const state = companySelectedCountry?.states?.find(s => s.id === event.target.value);
        setCompanyAddress({ ...companyAddress, stateId: state?.id });
    };

    const companyCityChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setCompanyAddress({ ...companyAddress, city: event.target.value });
    };

    const companyPostalCodeChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setCompanyAddress({ ...companyAddress, postalCode: event.target.value });
    };

    // Other functions
    const modalCloseHandler = () => {
        setIsModalOpen(false);
        clearForm();
        window.location.reload();
    };

    const continueButtonClickHandler = () => {
        setIsModalOpen(false);
        clearForm();
        window.location.reload();
    };

    const clearForm = () => {
        setFormLoaded(false);
        setIsSubmitting(false);
        getSelectedLocation();
        setLocationError("");
        setAccountNumber("");
        setFirstName("");
        setFirstNameError("");
        setLastName("");
        setLastNameError("")
        setEmailAddress("");
        setEmailError("")
        setExistingCustomer("");
        setExistingCustomerError("")
        setCompanyName("");
        setCompanyNameError("");
        setTradeOrDbaName("");
        setTradeOrDbaNameError("");
        setCompanyAddress({});
        setCompanyCountryError("");
        setCompanyAddress1Error("")
        setCompanyAddress2Error("");
        setCompanyCityError("");
        setCompanyStateError("");
        setCompanyPostalCodeError("");
        setPhoneError("");
        setPhoneNumber("");
        setFaxError("");
        setFaxNumber("");
    }

    const populateModel = () => {
        const model: AccountRequestModel = {
            firstName,
            lastName,
            emailAddress,
            existingCustomer,
            accountNumber,
            companyName,
            tradeOrDbaName,
            companyAddress,
            phoneNumber,
            faxNumber,            
            location: selectedLocation || "",
        }
        return model;
    }

    const validateForm = async () => {
        const validateFormResult: boolean[] = [
            validateFirstName(),
            validateLastName(),
            validateLocation(),
            validateEmail(),
            validateCompanyName(),
            validateExistingCustomer(),
        ];

        if (existingCustomer === "Yes") {
            validateFormResult.push(validateAccountNumber())
        }

        if (existingCustomer === "No") {
            validateFormResult.push(
                validateTradeOrDbaName(),
                validateCompanyCountry(),
                companySelectedCountry && companySelectedCountry.states && companySelectedCountry.states.length > 0
                    ? validateCompanyState()
                    : validateCompanyStateName(),
                validateCompanyAddress1(),
                validateCompanyAddress2(),
                validateCompanyCity(),
                validateCompanyPostalCode(),
                validatePhone(),
                validateFax()
            )
        }
        if (!validateFormResult.includes(false)) {
            const isReCaptchaValid = await validateReCaptcha({ location: "AccountRequest", recaptchaRef });
            if (!isReCaptchaValid) return;
            setIsSubmitting(true);
            const accountRequestModel = populateModel();

            ucsAccountRequest({
                accountRequestModel,
                onSuccess: () => {   
                    if(redirectOnSubmit.value){
                        history.push(redirectOnSubmit.value);
                    }  
                    else{
                        setIsModalOpen(true);
                    }  
                    setIsSubmitting(false);
                },
                onError: () => {
                    toasterContext.addToast({
                        body: siteMessage("Account_Request_Failure"),
                        messageType: "danger",
                    });
                    clearForm();
                },
                onComplete() {
                    // "this" is targeting the object being created, not the parent SFC
                     
                    // this.onSuccess?.();
                },
            });
        }
    }

    return (
        <>
            <form id="accountRequestForm" noValidate style={{ width: "100%" }}>
                <StyledWrapper {...styles.formWrapper}>
                    <StyledWrapper {...styles.formGroup}>
                        <TextField
                            {...styles.textFieldStyles}
                            label="First Name"
                            required={true}
                            placeholder=""
                            value={firstName || ""}
                            error={firstNameError}
                            onChange={firstNameChangeHandler}
                            autoComplete="off"
                            autoCorrect="off"
                            spellCheck={false}
                        />

                        <TextField
                            {...styles.textFieldStyles}
                            label="Last name"
                            required={true}
                            placeholder=""
                            value={lastName || ""}
                            error={lastNameError}
                            onChange={lastNameChangeHandler}
                            autoComplete="off"
                            autoCorrect="off"
                            spellCheck={false}
                        />

                    </StyledWrapper>
                    <StyledWrapper {...styles.formGroup}>
                        <TextField
                            {...styles.textFieldStyles}
                            label="LEGAL Company Name"
                            required={true}
                            placeholder=""
                            value={companyName}
                            error={companyNameError}
                            onChange={companyNameChangeHandler}
                            autoComplete="off"
                            autoCorrect="off"
                            spellCheck={false}
                        />

                        <TextField
                            {...styles.textFieldStyles}
                            type="email"
                            label="Email address"
                            required={true}
                            placeholder=""
                            value={emailAddress}
                            onChange={emailChangeHandler}
                            onBlur={emailBlurHandler}
                            error={emailError}
                            autoComplete="off"
                            autoCorrect="off"
                            spellCheck={false}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.formGroup}>
                        <Select
                            {...styles.locationSelect}
                            label={translate("Nearest Location")}
                            required={true}
                            value={selectedLocation || ""}
                            error={locationError}
                            onChange={event => locationChangeHandler(event.target.value)}
                            data-test-selector="nearby_location_dropdown"
                        >
                            <option value="">{translate("Select Nearest Location")}</option>
                            {options.map(option => (
                                <option key={option.optionValue} value={option.optionValue}>
                                    {option.optionText}
                                </option>
                            ))}
                        </Select>
                    </StyledWrapper>
                    <StyledWrapper {...styles.formGroupFullWidth}>
                        <Select
                            {...styles.selectStyles}
                            label={translate("Are you an existing customer of Upper Canada or Sierra?")}
                            value={existingCustomer}
                            required={true}
                            onChange={existingCustomerChangeHandler}
                            error={existingCustomerError}
                        >
                            <option value="">{translate("Make a selection")}</option>
                            {
                                isExistingCustomerOptions.map(title => (
                                    <option key={title} value={title}>
                                        {title}
                                    </option>
                                ))}
                        </Select>
                    </StyledWrapper>

                    {existingCustomer === "Yes" && (<StyledWrapper {...styles.formGroup}>
                        <TextField
                            {...styles.textFieldStyles}
                            label="Please enter your Account Number"
                            required={true}
                            placeholder=""
                            value={accountNumber}
                            error={accountNumberError}
                            onChange={accountNumberChangeHandler}
                            autoComplete="off"
                            autoCorrect="off"
                            spellCheck={false}
                        />
                    </StyledWrapper>)}

                    {existingCustomer === "No" && (
                        <>
                            <StyledWrapper {...styles.formGroup}>
                                <TextField
                                    {...styles.textFieldStyles}
                                    label="Trade/DBA Name"
                                    required={true}
                                    placeholder=""
                                    value={tradeOrDbaName}
                                    error={tradeOrDbaNameError}
                                    onChange={tradeOrDbaNameChangeHandler}
                                    autoComplete="off"
                                    autoCorrect="off"
                                    spellCheck={false}
                                />

                                <Select
                                    {...styles.selectStyles}
                                    label={translate("Country")}
                                    value={companyAddress.countryId}
                                    required={true}
                                    onChange={companyCountyChangeHandler}
                                    error={companyCountryError}
                                >
                                    <option value="">{translate("Make a selection")}</option>
                                    {
                                        countries?.map(country => (
                                            <option key={country.name} value={country.id}>
                                                {country.name}
                                            </option>
                                        ))}
                                </Select>

                                {!companySelectedCountry || companySelectedCountry.states!.length > 0 ? (
                                    <Select
                                        {...styles.selectStyles}
                                        label={translate("State / Province")}
                                        value={companyAddress.stateId}
                                        required={true}
                                        onChange={companyStateChangeHandler}
                                        error={companyStateError}
                                        disabled={!companySelectedCountry}
                                    >
                                        <option value="">{translate("Make a selection")}</option>
                                        {
                                            companySelectedCountry?.states?.map(st => (
                                                <option key={st.id} value={st.id}>
                                                    {st.name}
                                                </option>
                                            ))}
                                    </Select>
                                ) : (
                                    <TextField
                                        {...styles.textFieldStyles}
                                        label={translate("State / Province")}
                                        value={companyAddress.stateName || ""}
                                        required={true}
                                        minLength={2}
                                        error={companyStateError}
                                        onChange={companyStateNameChangeHandler}
                                        autoComplete="off"
                                        autoCorrect="off"
                                        spellCheck={false}
                                    />
                                )}

                                <TextField
                                    {...styles.textFieldStyles}
                                    label="Address Line 1"
                                    required={true}
                                    placeholder=""
                                    value={companyAddress.addressLine1}
                                    error={companyAddress1Error}
                                    onChange={companyAddress1ChangeHandler}
                                    autoComplete="off"
                                    autoCorrect="off"
                                    spellCheck={false}
                                />

                                <TextField
                                    {...styles.textFieldStyles}
                                    label="Address Line 2 (Optional)"
                                    placeholder=""
                                    value={companyAddress.addressLine2}
                                    error={companyAddress2Error}
                                    onChange={companyAddress2ChangeHandler}
                                    autoComplete="off"
                                    autoCorrect="off"
                                    spellCheck={false}
                                />
                                <TextField
                                    {...styles.textFieldStyles}
                                    label="City"
                                    placeholder=""
                                    value={companyAddress.city}
                                    error={companyCityError}
                                    onChange={companyCityChangeHandler}
                                    autoComplete="off"
                                    autoCorrect="off"
                                    spellCheck={false}
                                />

                                <TextField
                                    {...styles.textFieldStyles}
                                    label="Zip/Postal Code"
                                    placeholder=""
                                    value={companyAddress.postalCode}
                                    error={companyPostalCodeError}
                                    onChange={companyPostalCodeChangeHandler}
                                    autoComplete="off"
                                    autoCorrect="off"
                                    spellCheck={false}
                                />
                                <TextField
                                    {...styles.textFieldStyles}
                                    label="Phone number"
                                    required={true}
                                    placeholder="_ _ _ - _ _ _ - _ _ _ _"
                                    value={phoneNumber}
                                    onChange={phoneChangeHandler}
                                    onBlur={phoneBlurHandler}
                                    error={phoneError}
                                    type="tel"
                                    autoComplete="off"
                                    autoCorrect="off"
                                    spellCheck={false}
                                />
                                <TextField
                                    {...styles.textFieldStyles}
                                    label="Fax (Optional)"
                                    required={true}
                                    placeholder="_ _ _ - _ _ _ - _ _ _ _"
                                    value={faxNumber}
                                    onChange={faxChangeHandler}
                                    onBlur={faxBlurHandler}
                                    error={faxError}
                                    type="tel"
                                    autoComplete="off"
                                    autoCorrect="off"
                                    spellCheck={false}
                                />
                            </StyledWrapper>
                        </>)}
                    <UcsReCaptcha recaptchaRef={recaptchaRef} location="AccountRequest" />
                    <StyledWrapper {...styles.buttonSection}>
                        <Button
                            onClick={e => {
                                e.preventDefault();
                                validateForm();
                            }}
                            disabled={isSubmitting}
                        >
                            {translate(submitButtonText)}
                        </Button></StyledWrapper>
                </StyledWrapper>
            </form>
            <Modal
                headline={translate(successTitle)}
                sizeVariant="small"
                isOpen={isModalOpen}
                handleClose={modalCloseHandler}
            >
                <GridContainer>
                    <GridItem width={12}>
                        <Typography>{successMessage}</Typography>
                    </GridItem>
                    <GridItem {...styles.buttonGridItem}>
                        <Button onClick={continueButtonClickHandler}>{translate("Close")}</Button>
                    </GridItem>
                </GridContainer>
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(AccountRequestRequestForm));